import { UseFormReturn } from "react-hook-form";

import Form from "../base/Form/Form";
import { Button } from "../base/Button";
import { FormTextField } from "../base/Form/FormTextField";
import { FormErrors } from "../base/Form/FormErrors";
import Modal, { ModalControlProps } from "../../layouts/Modal";
import { IUser } from "../../models/User/User";
import useCustomerUsersMutations from "../../data/CustomerUser/useCustomerUsersMutations";

interface ChangeUserPasswordForm {
  password: string;
  confirmPassword: string;
}

interface ChangeUserPasswordModalProps extends ModalControlProps {
  customerId: string;
  user?: IUser;
}

export function ChangeUserPasswordModal({
  open,
  setOpen,
  customerId,
  user,
}: ChangeUserPasswordModalProps) {
  const { setPassword } = useCustomerUsersMutations(customerId);

  const onSubmit = async (data: ChangeUserPasswordForm) => {
    if (!user) return;

    try {
      await setPassword.mutateAsync({
        userId: user.id,
        password: data.password,
      });
      setOpen(false);
    } catch {
      // The mutation surfaces the failure.
    }
  };

  const displayName = [user?.forename, user?.surname].filter(Boolean).join(" ");

  return (
    <Modal open={open} setOpen={setOpen} width="!w-1/3">
      <div>
        <div className="border-b">
          <h2 className="text-lg font-stick font-medium leading-6 mb-1 text-gray-900">
            Change password
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {displayName
              ? `Set a new password for ${displayName}.`
              : "Set a new password for this user."}
          </p>
        </div>
        <Form<ChangeUserPasswordForm>
          key={`${user?.id ?? "none"}-${open ? "open" : "closed"}`}
          defaultValues={{ password: "", confirmPassword: "" }}
          onSubmit={onSubmit}
          className="pt-4 grid grid-cols-1 gap-y-4"
        >
          {({
            register,
          }: UseFormReturn<ChangeUserPasswordForm>) => (
            <>
              <div className="-m-4">
                <FormErrors />
              </div>
              <FormTextField
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                  pattern: {
                    value: /\d/,
                    message: "Password must contain at least 1 number",
                  },
                })}
                type="password"
                label="Password"
                autoComplete="new-password"
              />
              <FormTextField
                {...register("confirmPassword", {
                  required: "Confirm Password is required",
                  validate: (value, formValues) =>
                    value === formValues.password ||
                    "Confirm Password must match Password",
                })}
                type="password"
                label="Confirm Password"
                autoComplete="new-password"
              />
              <Button type="submit" disabled={setPassword.isPending}>
                {setPassword.isPending ? "Updating..." : "Update password"}
              </Button>
            </>
          )}
        </Form>
      </div>
    </Modal>
  );
}
